import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Axios from 'axios';
import moment from 'moment';
import intlShape from '@/shared/utils/intlShape';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';

import SimplePanel from '@/shared/components/SimplePanel';
import SignalRService from '@/shared/services/SignalRService';
import Button from '@/shared/components/Button';
import { FaFileExcel } from 'react-icons/fa';
import { mapRegTaskName } from '@/shared/services/ObjectDisplayService';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import RelationSearch from '@/shared/components/input/RelationSearch';
import TextSearch from '@/shared/components/input/TextSearch';
import DataCacheService from '@/shared/services/DataCacheService';

import FormattedDateTime from '../../shared/components/FormattedDateTime';
import LoadingAnimation from '../../shared/components/LoadingAnimation';
import { ColumnHeader, ScrollableTable } from '../../shared/components/table';
import api from '../../shared/api';
import config from '../../config';
import messages from './jobMessages';
import JobStatus from './JobStatus';

import { injectStateService, StateService } from '../../shared/services/StateService';
import JobDetailUpdateTaskStatus from './jobDetails/JobDetailUpdateTaskStatus';
import JobDetailRelationFieldUpdate from './jobDetails/JobDetailRelationFieldUpdate';
import JobDetailChangeTaskField from './jobDetails/JobDetailChangeTaskField';
import JobDetailChangeTaskParameterValue from './jobDetails/JobDetailChangeTaskParameterValue';
import JobDetailCreateTaskSchedule from './jobDetails/JobDetailCreateTaskSchedule';
import JobDetailCreateRegistration from './jobDetails/JobDetailCreateRegistration';
import JobDetailSetInvoiceSchedule from './jobDetails/JobDetailSetInvoiceSchedule';
import JobDetailMailInvoices from './jobDetails/JobDetailMailInvoices';
import JobDetailSendRelationMail from './jobDetails/JobDetailSendRelationMail';
import JobDetailDeleteTaskSchedule from './jobDetails/JobDetailDeleteTaskSchedule';
import JobDetailUpdateDeviationDuration from './jobDetails/JobDetailUpdateDeviationDuration';
import JobDetailChangeTaskTags from './jobDetails/JobDetailChangeTaskTags';
import JobDetailPriceGridUpdate from './jobDetails/JobDetailPriceGridUpdate';
import JobDetailReprocessInvoiceStreamSchedule from './jobDetails/JobDetailReprocessInvoiceStreamSchedule';

import AuthService, { functionalities, privileges } from '../../shared/services/AuthService';
import getLabelForJobEntity from './getLabelForJobEntity';
import dateFormatService from '../../shared/services/DateFormatService';
import JobDetailInitAbsenceCounter from './jobDetails/JobDetailInitAbsenceCounter';
import JobDetailRelationSoftwareUpdate from './jobDetails/JobDetailRelationSoftwareUpdate';
import JobDetailBatchFillDays from './jobDetails/JobDetailBatchFillDays';
import JobDetailUpdateInvoiceStreamField from './jobDetails/JobDetailUpdateInvoiceStreamField';

const tryParseJSON = jsonString => {
  try {
    const o = JSON.parse(jsonString);

    // Handle non-exception-throwing cases:
    // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
    // but... JSON.parse(null) returns null, and typeof null === "object",
    // so we must check for that, too. Thankfully, null is falsey, so this suffices:
    if (o && typeof o === 'object') {
      return o;
    }
  } catch (e) {
    return jsonString;
  }

  return jsonString;
};

class JobDetailContainer extends Component {
  state = {
    job: null,
    items: [],
    initialSearchParams: {
      sortKey: 'displayName',
      desc: false,
      extraFilters: {
        relationId: null,
        relationName: null,
        searchInfo: null,
      },
    },
  };

  static propTypes = {
    jobId: PropTypes.string.isRequired,
    intl: intlShape.isRequired,
    stateService: PropTypes.instanceOf(StateService).isRequired,
  };

  componentDidMount() {
    const { jobId } = this.props;
    if (!jobId) return;

    const endpoint = `${config.appUrls.jobs}/api/Jobs/${jobId}`;

    api.base.get(endpoint).then(({ data }) => {
      const parameters = data.parameters ? tryParseJSON(data.parameters) : {};

      this.setState(state => ({
        ...state,
        job: { ...data, parameters },
      }));
    });

    SignalRService.subscribe('jobUpdated', this.jobUpdateHandler);
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('jobUpdated', this.jobUpdateHandler);
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  jobUpdateHandler = job => this.updateJob(job);

  createExcelReport = jobId => {
    const { intl } = this.props;

    const languageCode = intl.locale;
    const signalRConnectionId = SignalRService.getConnectionId();
    return api.base
      .get(`${config.appUrls.jobs}/api/jobs/export/${jobId}/${languageCode}/${signalRConnectionId}`)
      .then(() => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  updateJob(job) {
    const { jobId } = this.props;
    if (job.id !== jobId) return;

    const parameters = job.parameters ? tryParseJSON(job.parameters) : {};

    this.setState(state => ({
      ...state,
      job: { ...job, parameters },
    }));
  }

  loadItems = searchParams => {
    const { jobId } = this.props;
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    this.cancelToken = Axios.CancelToken.source();

    const search = JSON.stringify(searchParams.search || {});
    const filter = JSON.stringify(searchParams.filter || {});

    const endpoint = `${config.appUrls.jobs}/api/Jobs/Entries/${jobId}`;

    return api.base
      .get(endpoint, {
        params: {
          desc: searchParams.desc,
          sort: searchParams.sortKey,
          page: searchParams.page,
          take: searchParams.pageSize,
          search,
          filter,
          ...searchParams.extraFilters,
        },
        cancelToken: this.cancelToken.token,
      })
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          items: searchParams.page > 0 ? [...state.items, ...data] : [...data],
          filter: searchParams.filter,
        }));
        return data;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  };
  filterChanged = filter => {
    const { initialSearchParams } = this.state;
    const { extraFilters } = initialSearchParams;
    const newFilters = { ...extraFilters, ...filter };
    if (JSON.stringify(extraFilters) === JSON.stringify(newFilters)) return;

    this.setState(state => ({
      initialSearchParams: {
        ...state.currentSearchParams,
        page: 0,
        extraFilters: newFilters,
      },
    }));
  };
  relationChanged = item => {
    if (!item || !item.key) {
      this.filterChanged({ relationId: null, relationName: null });
    } else {
      this.filterChanged({
        relationId: item.key,
        relationName: item.label,
      });
    }
  };
  getEntityColumnLabel = () => {
    const { intl } = this.props;
    const { job } = this.state;

    const label = getLabelForJobEntity(intl, job.jobType, false);
    return label;
  };

  parseErrorMessage = error => {
    if (!error) return;
    const { intl } = this.props;

    try {
      const errorObject = JSON.parse(error);
      const parameters = {};
      if (errorObject.parameters && errorObject.parameters.length > 0) {
        for (let i = 0; i < errorObject.parameters.length; i++) {
          parameters[`p${i}`] = errorObject.parametersEnumType // if the parameters are of a specific enum type, we will translate it accordingly.
            ? Array.isArray(errorObject.parameters[i])
              ? errorObject.parameters[i]
                  .map(x => intl.formatMessage(listItemMsg(errorObject.parametersEnumType, x)))
                  .join(', ')
              : intl.formatMessage(
                  listItemMsg(errorObject.parametersEnumType, errorObject.parameters[i]),
                )
            : errorObject.parameters[i] // try to translate parameter in case its translatable
            ? intl.formatMessage({
                id: errorObject.parameters[i],
                defaultMessage: errorObject.parameters[i],
              })
            : '';
        }
      }
      return (
        <FormattedListValue list={errorObject.type} id={errorObject.code} values={parameters} />
      );
    } catch (exception) {
      return error;
    }
  };

  goToRelationByInvoiceStream = item => {
    const { stateService } = this.props;

    const endpoint = `${config.appUrls.api}/api/relation/invoicestreams`;

    const invoiceStreamId = item.entityId;

    api.base
      .post(endpoint, [invoiceStreamId])
      .then(({ data }) => {
        if (data && data.length > 0) {
          const relation = data[0];
          stateService.gotoRelation(relation.id);
        }
      })
      .catch(error => {
        console.error(error);
      });
  };

  renderEntity = item => {
    const { stateService, intl } = this.props;

    if (item.entityId) {
      switch (item.entityType) {
        // invoicestream
        case 0:
          return (
            <span className="clickable" onClick={() => this.goToRelationByInvoiceStream(item)}>
              <span style={{ verticalAlign: 'middle' }}>
                <i className="fa fa-external-link " />
              </span>
              &nbsp; {item.displayName}
            </span>
          );
        // relation
        case 1:
          return (
            <span
              className="clickable"
              onClick={() => stateService.gotoRelation(item.relationId || item.entityId)}
            >
              <span style={{ verticalAlign: 'middle' }}>
                <i className="fa fa-external-link " />
              </span>
              &nbsp; {item.displayName}
            </span>
          );
        case 2: // invoice
          if (
            AuthService.hasPermission({
              functionality: functionalities.InvoiceFinal,
              privilege: privileges.Read,
            })
          )
            return (
              <span>
                <span
                  className="clickable"
                  title={intl.formatMessage(messages.OpenInvoice)}
                  onClick={() => stateService.gotoInvoiceOverview({ invoiceId: item.entityId })}
                >
                  <i className="fa fa-file-o" />
                  &nbsp; {item.displayName}
                </span>
                {!!item.relationId && (
                  <span
                    className="clickable margin10L"
                    title={intl.formatMessage(messages.OpenRelation)}
                    onClick={() => stateService.gotoRelation(item.relationId)}
                  >
                    <span style={{ verticalAlign: 'middle' }}>
                      <i className="fa fa-external-link " />
                    </span>
                  </span>
                )}
              </span>
            );
          break;
        case 4: //ResourceCategory
          return (
            <FormattedListValue
              list="ResourceCategory"
              id={item.entityId}
              fallback={item.displayName}
            />
          );
        // Document
        case 5:
          return <span>{item.displayName}</span>;
        default:
          break;
      }
    }
    if (item.relationId) {
      return (
        <span className="clickable" onClick={() => stateService.gotoRelation(item.relationId)}>
          <i className="fa fa-external-link" />
          &nbsp; {item.displayName}
        </span>
      );
    }

    return item.displayName;
  };

  renderJobDetail = job => {
    switch (job.jobType) {
      case 0:
        return <JobDetailCreateTaskSchedule job={job} />;
      case 1:
        return <JobDetailRelationFieldUpdate job={job} />;
      case 2:
        return <JobDetailUpdateTaskStatus job={job} />;
      case 3:
        return <JobDetailChangeTaskField job={job} />;
      case 4:
      case 6:
        return <JobDetailCreateRegistration job={job} />;
      case 7:
        return <JobDetailChangeTaskParameterValue job={job} />;
      case 8:
        return <JobDetailSetInvoiceSchedule job={job} />;
      case 9:
        return <JobDetailMailInvoices job={job} />;
      case 12:
        return <JobDetailSendRelationMail job={job} />;
      case 18:
        return <JobDetailDeleteTaskSchedule job={job} />;
      case 47:
      case 48:
        return <JobDetailChangeTaskTags job={job} />;
      case 53:
        return <JobDetailInitAbsenceCounter job={job} />;
      case 56:
        return <JobDetailPriceGridUpdate job={job} />;
      case 57:
        return <JobDetailReprocessInvoiceStreamSchedule job={job} />;
      case 61:
        return <JobDetailRelationSoftwareUpdate job={job} />;
      case 73:
        return <JobDetailBatchFillDays job={job} />;
      case 75:
        return <JobDetailUpdateInvoiceStreamField job={job} />;
      case 80:
      case 81:
        return <JobDetailUpdateDeviationDuration job={job} />;
      default:
        return <span />;
    }
  };

  renderInfo = item => {
    const { job } = this.state;
    const { intl } = this.props;
    switch (job.jobType) {
      case 36: {
        const task = tryParseJSON(item.parametersJson);
        const taskName = mapRegTaskName(task, intl);
        if (!taskName) return null;
        return `${taskName} - ${dateFormatService.formatDate(task.deadline)}`;
      }
      case 38: {
        return item.infoDescription
          ? item.infoDescription.replace(
              'registrations',
              intl.formatMessage(messages.Registrations),
            )
          : item.infoDescription;
      }
      default:
        return item.infoDescription;
    }
  };

  renderRow = (item, intl) => (
    <tbody key={item.id} className="hover" style={{ whiteSpace: 'pre-line' }}>
      <tr>
        <td>{this.renderEntity(item)}</td>
        <td>
          <JobStatus status={item.status} intl={intl} />
        </td>
        <td>{item.completedAt && <FormattedDateTime value={item.completedAt} />}</td>
        <td>{this.renderInfo(item)}</td>
        <td>{this.parseErrorMessage(item.error)}</td>
        <td />
      </tr>
    </tbody>
  );

  render() {
    const { intl } = this.props;
    const { job, items, initialSearchParams } = this.state;
    if (!job) return <LoadingAnimation />;

    const entityColumnLabel = this.getEntityColumnLabel();
    const user = DataCacheService.getUserName(job.createdBy);
    const { extraFilters } = initialSearchParams;
    let initRelation = null;
    if (extraFilters.relationId && extraFilters.relationName) {
      initRelation = {
        key: extraFilters.relationId,
        label: extraFilters.relationName,
      };
    }

    return (
      <div className="row">
        <div className="col-md-4">
          <div className="job-panel-wrapper">
            <SimplePanel
              title={`${intl.formatMessage(messages.JobDetails)} ${intl.formatMessage({
                id: `jobtype_${job.jobType}`.toLowerCase(),
                defaultMessage: '',
              })}`}
            >
              <div className="info-block container-fluid">
                <div className="row">
                  <div className="col-lg-6">
                    <div className="row">
                      <div className="col-md-5 col-sm-6">
                        <label>
                          <b>
                            <FormattedMessage {...messages.JobStatus} />
                          </b>
                        </label>
                      </div>
                      <div className="col-md-7 col-sm-6">
                        <JobStatus status={job.status} intl={intl} />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-5 col-sm-6">
                        <label>
                          <b>
                            <FormattedMessage {...messages.CreatedBy} />
                          </b>
                        </label>
                      </div>
                      <div className="col-md-7 col-sm-6">{user}</div>
                    </div>
                    <div className="row">
                      <div className="col-md-5 col-sm-6">
                        <label>
                          <b>
                            <FormattedMessage {...messages.CreatedAt} />
                          </b>
                        </label>
                      </div>
                      <div className="col-md-7 col-sm-6">
                        <FormattedDateTime value={job.createdAt} />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-5 col-sm-6">
                        <label>
                          <b>
                            <FormattedMessage {...messages.CompletedAt} />
                          </b>
                        </label>
                      </div>
                      <div className="col-md-7 col-sm-6">
                        {job.completedAt && <FormattedDateTime value={job.completedAt} />}
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-5">
                    <div className="row">
                      <div className="col-md-8 col-sm-8">
                        <label>
                          <b>
                            <FormattedMessage {...messages.NumberOfItems} />
                          </b>
                        </label>
                      </div>
                      <div className="col-md-4 col-sm-4">{job.totalEntries}</div>
                    </div>
                    <div className="row">
                      <div className="col-md-8 col-sm-8">
                        <label>
                          <b>
                            <FormattedMessage {...messages.CompletedItems} />
                          </b>
                        </label>
                      </div>
                      <div className="col-md-4 col-sm-4">{job.completedEntries}</div>
                    </div>
                    {job.skippedEntries > 0 && (
                      <div className="row">
                        <div className="col-md-8 col-sm-8">
                          <label>
                            <b>
                              <FormattedMessage {...messages.SkippedItems} />
                            </b>
                          </label>
                        </div>
                        <div className="col-md-4 col-sm-4">{job.skippedEntries}</div>
                      </div>
                    )}
                    <div className="row">
                      <div className="col-md-8 col-sm-8">
                        <label>
                          <b>
                            <FormattedMessage {...messages.FailedItems} />
                          </b>
                        </label>
                      </div>
                      <div className="col-md-4 col-sm-4">{job.failedEntries}</div>
                    </div>
                  </div>
                </div>
              </div>
            </SimplePanel>
          </div>
          <div className="job-panel-wrapper">{this.renderJobDetail(job)}</div>
        </div>

        <div className="col-md-8 ">
          <div className="row">
            <div className="col-md-4">
              <TextSearch
                className="form-control"
                value={extraFilters.searchInfo}
                placeholder={intl.formatMessage(messages.SearchInfo)}
                onChange={value => this.filterChanged({ searchInfo: value })}
              />
            </div>
            <div className="col-md-4">
              <RelationSearch onChange={this.relationChanged} initialSelectedItem={initRelation} />
            </div>
            <div className="col-md-4 pull-right content-right">
              <Button
                btnClassName="btn btn-xs btn-inverse margin10R"
                onClick={() => this.createExcelReport(job.id)}
                showProgress
              >
                <span className="margin5R">
                  <FaFileExcel className="file-icon" />
                </span>
                {intl.formatMessage(messages.ExportToExcel)}
              </Button>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <ScrollableTable
                tableClassName="table-grid margin50B"
                loadItems={searchParams => this.loadItems(searchParams)}
                initialSearchParams={initialSearchParams}
                items={items}
                renderRow={item => this.renderRow(item, intl)}
                noDataLabel={<FormattedMessage {...messages.NoJobsFound} />}
              >
                <ColumnHeader title={entityColumnLabel} sortKey="displayName" />
                <ColumnHeader
                  title={intl.formatMessage(messages.JobStatus)}
                  sortKey="status"
                  filter={{
                    type: 'items',
                    key: `status`,
                    items: [0, 1, 2, 3, 4],
                    renderItem: item => <JobStatus status={item} intl={intl} />,
                  }}
                />

                <ColumnHeader
                  title={intl.formatMessage(messages.CompletedAt)}
                  sortKey="completedAt"
                />
                <ColumnHeader title={intl.formatMessage(messages.Info)} />
                <ColumnHeader title={intl.formatMessage(messages.Errors)} />
                <ColumnHeader />
              </ScrollableTable>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default injectStateService(injectIntl(JobDetailContainer));
